import { useRef, useState, DragEvent, ChangeEvent } from "react";
import { Button } from "@/components/ui/button";
import { Upload, X, Save, RefreshCw, ImageOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

export type MediaManagerProps = {
  title?: string;
  currentImageUrl?: string | null;
  accept?: string[]; // mime types
  maxSizeMb?: number;
  uploading?: boolean;
  onSave: (file: File) => Promise<void>;
};

const DEFAULT_ACCEPT = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

export function MediaManager({
  title = "Thumbnail & Cover Image",
  currentImageUrl,
  accept = DEFAULT_ACCEPT,
  maxSizeMb = 10,
  uploading = false,
  onSave,
}: MediaManagerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [currentBroken, setCurrentBroken] = useState(false);

  const acceptExts = "PNG, JPG, JPEG, WebP";

  const validate = (file: File): string | null => {
    if (!accept.includes(file.type)) return `Unsupported file type. Accepted: ${acceptExts}.`;
    if (file.size > maxSizeMb * 1024 * 1024) return `File is too large. Maximum size is ${maxSizeMb} MB.`;
    return null;
  };

  const pickFile = (file: File) => {
    const err = validate(file);
    if (err) { toast.error(err); return; }
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    setPendingFile(file);
    setPendingUrl(URL.createObjectURL(file));
  };

  const onBrowse = () => inputRef.current?.click();
  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) pickFile(f);
    e.target.value = "";
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  };
  const clearPending = () => {
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    setPendingFile(null); setPendingUrl(null);
  };
  const save = async () => {
    if (!pendingFile) return;
    try {
      await onSave(pendingFile);
      clearPending();
    } catch {
      /* onSave surfaces its own toast */
    }
  };

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-base font-semibold">{title}</h3>
        <span className="text-xs text-muted-foreground">Max {maxSizeMb} MB · {acceptExts}</span>
      </div>

      <div>
        <div className="text-xs font-medium text-muted-foreground mb-1">Current image</div>
        {currentImageUrl && !currentBroken ? (
          <img
            src={currentImageUrl}
            alt="Current thumbnail"
            className="w-full max-h-56 object-cover rounded border"
            onError={() => setCurrentBroken(true)}
          />
        ) : (
          <div className="w-full h-32 rounded border border-dashed flex items-center justify-center text-sm text-muted-foreground gap-2 bg-muted/30">
            <ImageOff className="h-4 w-4" />
            No valid thumbnail is currently uploaded.
          </div>
        )}
      </div>

      {!pendingFile ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/30"
          }`}
        >
          <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm font-medium">Drag image here</p>
          <p className="text-xs text-muted-foreground mt-1">or</p>
          <Button type="button" variant="outline" size="sm" className="mt-2" onClick={onBrowse}>
            Browse Files
          </Button>
          <p className="text-xs text-muted-foreground mt-3">
            Accepted formats: {acceptExts} · Maximum size: {maxSizeMb} MB
          </p>
          <input
            ref={inputRef}
            type="file"
            accept={accept.join(",")}
            className="hidden"
            onChange={onInputChange}
          />
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1">Pending image preview</div>
            <img src={pendingUrl!} alt="Pending" className="w-full max-h-56 object-cover rounded border" />
            <p className="text-xs text-muted-foreground mt-1 truncate">
              {pendingFile.name} · {(pendingFile.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={save} disabled={uploading}>
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save as Class Thumbnail
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onBrowse} disabled={uploading}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Replace Image
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={clearPending} disabled={uploading}>
              <X className="h-4 w-4 mr-2" />
              Remove Pending Image
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={clearPending} disabled={uploading}>
              Cancel
            </Button>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={accept.join(",")}
            className="hidden"
            onChange={onInputChange}
          />
        </div>
      )}
    </div>
  );
}

export default MediaManager;
